/**
 * useKnativePipelineActions - Knative CD Pipeline 触发逻辑（可复用 hook）
 *
 * 封装 Knative 发版 / 切流的触发逻辑（对话框 + 参数注入 + createPipelineRun），
 * 供 CD Pipelines tab（KnativeWorkflowsContent）及 Serving tab 卡片按钮使用。
 *
 * 触发/校验/参数注入逻辑与最初的内嵌实现完全一致，仅做编排抽取。
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  makeStyles,
  Tooltip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  LinearProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes-react';
import { useTektonPipelines } from '../tekton/shared';
import { getPipelineRunStatus, getStatusColor, formatTime, formatDuration } from '../tekton/types';

// Knative 工作流类型常量
const KNATIVE_WORKFLOW_TYPE = 'KnativeWorkflow';

const useStyles = makeStyles(theme => ({
  statusDots: {
    display: 'flex',
    gap: 6,
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
    transition: 'transform 0.15s',
    '&:hover': {
      transform: 'scale(1.4)',
    },
  },
  cardRow2: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(2),
    '& .MuiTypography-root': {
      fontWeight: 'bold',
    },
  },
  paramField: {
    marginBottom: theme.spacing(2),
  },
  summaryBox: {
    padding: theme.spacing(1.5),
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    marginBottom: theme.spacing(2),
  },
}));

export interface KnativePipelineActions {
  /** Pipeline 列表是否加载中 */
  pipelinesLoading: boolean;
  /** 手动刷新 pipelines + runs */
  refresh: () => Promise<void>;
  /** 正在执行的 Pipeline 名称集合（含刚触发的静默期，用于即时反馈） */
  executingPipelines: Set<string>;
  /** 是否存在 Knative 注解配置（无则不应渲染触发入口） */
  hasKnativeConfig: boolean;
  /** 找到的 release / traffic-split Pipeline（未找到则按钮应禁用） */
  releasePipeline: any;
  trafficSplitPipeline: any;
  pipelines: any[];
  /** 打开发版 / 切流对话框 */
  openReleaseDialog: () => void;
  openTrafficDialog: () => void;
  /** 全部 / 分类的 PipelineRuns */
  pipelineRuns: any[];
  releaseRuns: any[];
  trafficRuns: any[];
  /** 渲染最近 N 次状态圆点 / 最近一次摘要 */
  renderStatusDots: (runs: any[], count?: number) => JSX.Element;
  renderLastRun: (runs: any[]) => JSX.Element;
  /** Pipeline 所在管控集群信息（供展示） */
  tektonClusterName: string;
  tektonNamespace: string;
  /** 两个对话框节点，消费方需渲染一次 */
  dialogs: React.ReactNode;
}

export const useKnativePipelineActions = (): KnativePipelineActions => {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const kubernetesApi = useApi(kubernetesApiRef);
  const alertApi = useApi(alertApiRef);

  // Pipeline 对话框状态
  const [releaseDialogOpen, setReleaseDialogOpen] = useState(false);
  const [trafficDialogOpen, setTrafficDialogOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executeError, setExecuteError] = useState<string>('');
  const [dialogLoading, setDialogLoading] = useState(false);

  // 发版参数
  const [releaseImage, setReleaseImage] = useState('');
  const [releaseRevisionName, setReleaseRevisionName] = useState('');

  // 切流参数
  const [trafficEntries, setTrafficEntries] = useState<Array<{ revisionName: string; percent: number }>>([]);

  // knative-project Entity JSON
  const [knativeProjectJson, setKnativeProjectJson] = useState('');

  // 懒加载的 Knative 数据（只在打开对话框时获取）
  const [knativeRevisions, setKnativeRevisions] = useState<any[]>([]);

  // 从 Entity 注解获取配置
  const annotations = entity?.metadata?.annotations || {};
  const knativeServiceName = annotations['knative.dev/service-name'] || '';
  const knativeNamespace = annotations['knative.dev/service-namespace'] || '';
  const clusterName = annotations['backstage.io/kubernetes-cluster-name'] || '';

  // Pipeline 所在的管控集群（固定在 tekton-control 的 tekton-pipelines namespace）
  const tektonClusterName = 'tekton-control';
  const tektonNamespace = 'tekton-pipelines';

  const componentName = entity?.metadata?.name || '';

  // 是否有正在运行的 PipelineRun（用于决定是否开启轮询）
  const [hasRunningRuns, setHasRunningRuns] = useState(false);

  // 获取 Pipeline 列表和 PipelineRuns
  const {
    pipelines,
    pipelineRuns,
    loading: pipelinesLoading,
    createPipelineRun,
    executingPipelines,
    refresh,
  } = useTektonPipelines({
    componentName,
    clusterName: tektonClusterName,
    namespace: tektonNamespace,
    workflowType: KNATIVE_WORKFLOW_TYPE as any,
    pollInterval: 15000,
    enablePolling: hasRunningRuns,
  });

  // 检测是否有正在运行的 Pipeline，有则开启轮询
  useEffect(() => {
    setHasRunningRuns(executingPipelines.size > 0);
  }, [executingPipelines]);

  // 找到 release 和 traffic-split Pipeline
  const releasePipeline = useMemo(() =>
    pipelines.find(p =>
      p.metadata.labels?.['pipeline.type'] === 'knative-release' ||
      p.metadata.name.includes('knative-release')
    ),
    [pipelines],
  );

  const trafficSplitPipeline = useMemo(() =>
    pipelines.find(p =>
      p.metadata.labels?.['pipeline.type'] === 'knative-traffic-split' ||
      p.metadata.name.includes('knative-traffic-split')
    ),
    [pipelines],
  );

  // 获取关联的 knative-project Resource
  // 链路: Component → spec.system → System → hose.io/knative-resource-{env} → Resource
  useEffect(() => {
    const fetchKnativeProject = async () => {
      try {
        // 1. 获取 Component 的环境标识和所属 System
        const env = annotations['ekuaibao.com/environment'] || '';
        const systemName = (entity?.spec as any)?.system;
        if (!systemName || !env) return;

        // 2. 从 Catalog 获取 System Entity
        const systemRef = systemName.includes(':')
          ? systemName
          : `system:default/${systemName}`;
        const systemEntity = await catalogApi.getEntityByRef(systemRef);
        if (!systemEntity) return;

        // 3. 从 System 的注解中获取环境对应的 knative-project Resource 引用
        // 注解格式: hose.io/knative-resource-{env} = "resource:default/knative-xxx"
        const systemAnnotations = systemEntity.metadata?.annotations || {};
        const resourceRef = systemAnnotations[`hose.io/knative-resource-${env}`];

        if (!resourceRef) {
          // 兜底：从 System 的 dependsOn 中找 knative-project Resource
          const deps = (systemEntity.spec as any)?.dependsOn || [];
          const knativeDep = deps.find((d: string) =>
            d.includes('knative-'),
          );
          if (knativeDep) {
            const depEntity = await catalogApi.getEntityByRef(knativeDep);
            if (depEntity && (depEntity.spec as any)?.type === 'knative-project') {
              setKnativeProjectJson(JSON.stringify(depEntity));
            }
          }
          return;
        }

        // 4. 获取 Resource Entity（knative-project）
        const resourceEntity = await catalogApi.getEntityByRef(resourceRef);
        if (resourceEntity) {
          setKnativeProjectJson(JSON.stringify(resourceEntity));
        }
      } catch {
        // 获取失败时留空，Pipeline 会跳过校验
      }
    };

    fetchKnativeProject();
  }, [entity, annotations, catalogApi]);

  // 懒加载 Knative 数据（只在打开对话框时调用）
  const fetchKnativeData = useCallback(async () => {
    let image = '';
    let traffic: any[] = [];
    let revs: any[] = [];

    if (!knativeServiceName || !knativeNamespace || !clusterName) {
      return { image, traffic, revisions: revs };
    }

    const targetCluster = clusterName;
    try {
      // 获取 Service
      const svcResp = await kubernetesApi.proxy({
        clusterName: targetCluster,
        path: `/apis/serving.knative.dev/v1/namespaces/${knativeNamespace}/services/${knativeServiceName}`,
      });
      if (svcResp.ok) {
        const svc = await svcResp.json();
        image = svc?.spec?.template?.spec?.containers?.[0]?.image || '';
        traffic = svc?.status?.traffic || [];
      }

      // 获取 Revisions
      const revResp = await kubernetesApi.proxy({
        clusterName: targetCluster,
        path: `/apis/serving.knative.dev/v1/namespaces/${knativeNamespace}/revisions`,
      });
      if (revResp.ok) {
        const data = await revResp.json();
        revs = (data.items || []).filter((r: any) =>
          r.metadata?.ownerReferences?.some(
            (ref: any) => ref.kind === 'Configuration' && ref.name === knativeServiceName,
          ),
        );
        revs.sort((a: any, b: any) =>
          new Date(b.metadata.creationTimestamp).getTime() - new Date(a.metadata.creationTimestamp).getTime()
        );
        setKnativeRevisions(revs);
      }
    } catch {
      // 失败时不阻塞
    }

    return { image, traffic, revisions: revs };
  }, [kubernetesApi, knativeServiceName, knativeNamespace, clusterName]);

  // ── 发版逻辑 ────────────────────────────────────────────────────────────────

  const openReleaseDialog = useCallback(async () => {
    setReleaseImage('');
    setReleaseRevisionName('');
    setExecuteError('');
    setDialogLoading(true);
    setReleaseDialogOpen(true);
    // 后台加载数据
    const { image } = await fetchKnativeData();
    setReleaseImage(image);
    setDialogLoading(false);
  }, [fetchKnativeData]);

  const executeRelease = async () => {
    if (!releasePipeline || !releaseImage.trim()) return;

    setExecuting(true);
    setExecuteError('');

    try {
      const params = [
        { name: 'service-name', value: knativeServiceName },
        { name: 'namespace', value: knativeNamespace },
        { name: 'cluster-name', value: clusterName },
        { name: 'image', value: releaseImage.trim() },
        { name: 'revision-name', value: releaseRevisionName.trim() },
        { name: 'knative-project', value: knativeProjectJson },
      ];

      await createPipelineRun(releasePipeline, params);
      setReleaseDialogOpen(false);
      alertApi.post({
        message: '新建版本工作流已触发，正在运行。前往「CD Pipelines」标签查看执行状态、阶段与日志。',
        severity: 'success',
        display: 'transient',
      });
    } catch (err) {
      setExecuteError(err instanceof Error ? err.message : '执行失败');
    } finally {
      setExecuting(false);
    }
  };

  // ── 切流逻辑 ────────────────────────────────────────────────────────────────

  const openTrafficDialog = useCallback(async () => {
    setTrafficEntries([]);
    setExecuteError('');
    setDialogLoading(true);
    setTrafficDialogOpen(true);
    // 后台加载数据
    const { traffic, revisions: revs } = await fetchKnativeData();
    // 从当前流量分配初始化
    const entries = traffic
      .filter((t: any) => t.revisionName)
      .map((t: any) => ({
        revisionName: t.revisionName,
        percent: t.percent || 0,
      }));

    entries.sort((a: any, b: any) => {
      const revA = revs.find((r: any) => r.metadata.name === a.revisionName);
      const revB = revs.find((r: any) => r.metadata.name === b.revisionName);
      const timeA = revA ? new Date(revA.metadata.creationTimestamp).getTime() : 0;
      const timeB = revB ? new Date(revB.metadata.creationTimestamp).getTime() : 0;
      return timeB - timeA;
    });

    if (entries.length === 0 && revs.length > 0) {
      entries.push({
        revisionName: revs[0]?.metadata?.name || '',
        percent: 100,
      });
    }

    setTrafficEntries(entries);
    setDialogLoading(false);
  }, [fetchKnativeData]);

  // 流量百分比联动调整（参考 KnativeTrafficCard 优化逻辑）
  const handleTrafficPercentChange = (index: number, newPercent: number) => {
    const updated = [...trafficEntries];

    // 如果只有一个 revision，直接设置为 100%
    if (updated.length === 1) {
      updated[index] = { ...updated[index], percent: 100 };
      setTrafficEntries(updated);
      return;
    }

    const clampedPercent = Math.max(0, Math.min(100, newPercent));
    updated[index] = { ...updated[index], percent: clampedPercent };

    // 计算其他项总和
    const otherTotal = updated.reduce(
      (sum, t, i) => (i === index ? sum : sum + t.percent),
      0,
    );

    // 按比例调整其他项使总和为 100%
    const remainingPercent = 100 - clampedPercent;
    if (otherTotal > 0 && remainingPercent >= 0) {
      const scale = remainingPercent / otherTotal;
      updated.forEach((t, i) => {
        if (i !== index) {
          updated[i] = { ...t, percent: Math.round(t.percent * scale) };
        }
      });
    }

    // 处理舍入误差
    const finalTotal = updated.reduce((sum, t) => sum + t.percent, 0);
    if (finalTotal !== 100 && updated.length > 1) {
      const adjustment = 100 - finalTotal;
      for (let i = 0; i < updated.length; i++) {
        if (i !== index) {
          updated[i] = { ...updated[i], percent: Math.max(0, Math.min(100, updated[i].percent + adjustment)) };
          break;
        }
      }
    }

    setTrafficEntries(updated);
  };

  const handleTrafficRevisionChange = (index: number, revisionName: string) => {
    const updated = [...trafficEntries];
    updated[index] = { ...updated[index], revisionName };
    setTrafficEntries(updated);
  };

  const addTrafficEntry = () => {
    const usedNames = new Set(trafficEntries.map(e => e.revisionName));
    const sorted = [...knativeRevisions].sort((a, b) =>
      new Date(b.metadata.creationTimestamp).getTime() - new Date(a.metadata.creationTimestamp).getTime()
    );
    const available = sorted.find(r => !usedNames.has(r.metadata.name));
    if (available) {
      setTrafficEntries([{ revisionName: available.metadata.name, percent: 0 }, ...trafficEntries]);
    }
  };

  const removeTrafficEntry = (index: number) => {
    const updated = trafficEntries.filter((_, i) => i !== index);
    // 删除后自动重分配流量使总和为 100%
    if (updated.length > 0) {
      const currentTotal = updated.reduce((sum, t) => sum + t.percent, 0);
      if (currentTotal > 0) {
        const scale = 100 / currentTotal;
        updated.forEach(t => {
          t.percent = Math.round(t.percent * scale);
        });
      } else {
        // 总和为 0 时平均分配
        const avg = Math.floor(100 / updated.length);
        updated.forEach((t, i) => {
          t.percent = i === updated.length - 1
            ? 100 - (avg * (updated.length - 1))
            : avg;
        });
      }
      // 处理舍入误差
      const finalTotal = updated.reduce((sum, t) => sum + t.percent, 0);
      if (finalTotal !== 100) {
        updated[0].percent += (100 - finalTotal);
      }
    }
    setTrafficEntries(updated);
  };

  const trafficTotal = trafficEntries.reduce((sum, e) => sum + e.percent, 0);

  const executeTrafficSplit = async () => {
    if (!trafficSplitPipeline || trafficTotal !== 100) return;

    setExecuting(true);
    setExecuteError('');

    try {
      const trafficConfig = trafficEntries.map(e => ({
        revisionName: e.revisionName,
        percent: e.percent,
      }));

      const params = [
        { name: 'service-name', value: knativeServiceName },
        { name: 'namespace', value: knativeNamespace },
        { name: 'cluster-name', value: clusterName },
        { name: 'traffic-config', value: JSON.stringify(trafficConfig) },
        { name: 'knative-project', value: knativeProjectJson },
      ];

      await createPipelineRun(trafficSplitPipeline, params);
      setTrafficDialogOpen(false);
      alertApi.post({
        message: '切流工作流已触发，正在运行。前往「CD Pipelines」标签查看执行状态、阶段与日志。',
        severity: 'success',
        display: 'transient',
      });
    } catch (err) {
      setExecuteError(err instanceof Error ? err.message : '执行失败');
    } finally {
      setExecuting(false);
    }
  };

  // 按 revision 名称降序排列（最新的在前）
  const sortedRevisions = useMemo(() =>
    [...knativeRevisions].sort((a, b) =>
      new Date(b.metadata.creationTimestamp).getTime() - new Date(a.metadata.creationTimestamp).getTime()
    ),
    [knativeRevisions],
  );

  // 可用于添加的 Revision（排除已选的）
  const getAvailableRevisions = useCallback(() => {
    const usedNames = new Set(trafficEntries.map(e => e.revisionName));
    return sortedRevisions.filter(r => !usedNames.has(r.metadata.name));
  }, [sortedRevisions, trafficEntries]);

  // 按 pipeline 分类最近的 runs
  const releaseRuns = useMemo(() =>
    pipelineRuns
      .filter(r => r.spec?.pipelineRef?.name?.includes('release'))
      .sort((a, b) =>
        new Date(b.status?.startTime || b.metadata.creationTimestamp).getTime() -
        new Date(a.status?.startTime || a.metadata.creationTimestamp).getTime()
      ),
    [pipelineRuns],
  );
  const trafficRuns = useMemo(() =>
    pipelineRuns
      .filter(r => r.spec?.pipelineRef?.name?.includes('traffic'))
      .sort((a, b) =>
        new Date(b.status?.startTime || b.metadata.creationTimestamp).getTime() -
        new Date(a.status?.startTime || a.metadata.creationTimestamp).getTime()
      ),
    [pipelineRuns],
  );

  // 渲染最近 N 次状态圆点
  const renderStatusDots = (runs: any[], count = 5) => {
    const recent = runs.slice(0, count);
    return (
      <Box className={classes.statusDots}>
        {recent.map((run, i) => {
          const status = getPipelineRunStatus(run);
          return (
            <Tooltip key={i} title={`${status} — ${formatTime(run.status?.startTime || run.metadata.creationTimestamp)}`}>
              <Box
                className={classes.statusDot}
                style={{ backgroundColor: getStatusColor(status) }}
              />
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  // 渲染最近一次 run 的摘要
  const renderLastRun = (runs: any[]) => {
    const last = runs[0];
    if (!last) return <Typography variant="caption" color="textSecondary">暂无执行记录</Typography>;
    const startTime = last.status?.startTime || last.metadata.creationTimestamp;
    const status = getPipelineRunStatus(last);
    const isCompleted = ['Succeeded', 'Failed', 'Cancelled'].includes(status);
    const endTime = last.status?.completionTime ||
      (last.status?.conditions?.find((c: any) => c.type === 'Succeeded') as any)?.lastTransitionTime;
    return (
      <Box className={classes.cardRow2}>
        <span>最近: {formatTime(startTime)}</span>
        <span>耗时 {formatDuration(startTime, endTime, isCompleted ? undefined : Date.now())}</span>
        <span>{last.metadata?.annotations?.['backstage.io/triggered-by']?.split('@')[0] || '-'}</span>
      </Box>
    );
  };

  const hasKnativeConfig = Boolean(knativeServiceName && knativeNamespace && clusterName);

  const dialogs = (
    <>
      {/* ── 发版对话框 ──────────────────────────────────────────────── */}
      <Dialog open={releaseDialogOpen} onClose={() => setReleaseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle className={classes.dialogTitle} disableTypography>
          <Typography variant="h6">🚀 发版 — 更新镜像</Typography>
        </DialogTitle>
        {dialogLoading && <LinearProgress />}
        <DialogContent style={{ paddingTop: 16 }}>
          {/* 自动注入的参数概览 */}
          <Box className={classes.summaryBox}>
            <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
              自动注入参数
            </Typography>
            <Box display="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Service:</strong> {knativeServiceName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Namespace:</strong> {knativeNamespace}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>集群:</strong> {clusterName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Pipeline:</strong> {releasePipeline?.metadata.name || '-'}
              </Typography>
            </Box>
          </Box>

          <Divider style={{ marginBottom: 16 }} />

          <TextField
            label="镜像地址"
            value={releaseImage}
            onChange={e => setReleaseImage(e.target.value)}
            fullWidth
            required
            variant="outlined"
            className={classes.paramField}
            helperText="完整镜像地址，例如 registry.example.com/myapp:v1.2.3"
            placeholder="registry.example.com/myapp:v1.2.3"
          />

          <TextField
            label="Revision 名称（可选）"
            value={releaseRevisionName}
            onChange={e => setReleaseRevisionName(e.target.value)}
            fullWidth
            variant="outlined"
            className={classes.paramField}
            helperText="留空自动生成"
          />

          {executeError && (
            <Alert severity="error" style={{ marginTop: 8 }}>{executeError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseDialogOpen(false)} disabled={executing}>取消</Button>
          <Button
            onClick={executeRelease}
            color="primary"
            variant="contained"
            disabled={executing || !releaseImage.trim()}
            startIcon={executing ? <CircularProgress size={16} /> : <PlayArrowIcon />}
          >
            {executing ? '提交中...' : '执行发版'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── 切流对话框 ──────────────────────────────────────────────── */}
      <Dialog open={trafficDialogOpen} onClose={() => setTrafficDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle className={classes.dialogTitle} disableTypography>
          <Typography variant="h6">🔀 切流 — 调整流量分配</Typography>
        </DialogTitle>
        {dialogLoading && <LinearProgress />}
        <DialogContent style={{ paddingTop: 16 }}>
          {/* 自动注入的参数概览 */}
          <Box className={classes.summaryBox}>
            <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
              自动注入参数
            </Typography>
            <Box display="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              <Typography variant="body2" color="textSecondary">
                <strong>Service:</strong> {knativeServiceName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Namespace:</strong> {knativeNamespace}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>集群:</strong> {clusterName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                <strong>Pipeline:</strong> {trafficSplitPipeline?.metadata.name || '-'}
              </Typography>
            </Box>
          </Box>

          <Divider style={{ marginBottom: 16 }} />

          {/* 流量配置表格 */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" color="textSecondary">
              流量设置:
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={addTrafficEntry}
              disabled={getAvailableRevisions().length === 0}
              color="primary"
              variant="outlined"
            >
              添加
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>修订版本</TableCell>
                  <TableCell align="right" style={{ width: 150 }}>流量比例 %</TableCell>
                  <TableCell style={{ width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {trafficEntries.map((entry, index) => {
                  // 可选的 revision = 当前选中的 + 未被其他行使用的，统一排序
                  const selectableRevs = sortedRevisions.filter(
                    r => r.metadata.name === entry.revisionName || !trafficEntries.some(e => e.revisionName === r.metadata.name),
                  );
                  const selectedRev = knativeRevisions.find(r => r.metadata.name === entry.revisionName);
                  const creationTime = selectedRev
                    ? new Date(selectedRev.metadata.creationTimestamp).toLocaleString('zh-CN', {
                        year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit', second: '2-digit',
                      })
                    : '';

                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <FormControl size="small" style={{ minWidth: 280 }}>
                          <Select
                            value={entry.revisionName}
                            onChange={e => handleTrafficRevisionChange(index, e.target.value as string)}
                            displayEmpty
                            variant="outlined"
                            renderValue={(value) => (
                              <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                                {value as string || '选择版本'}
                              </Typography>
                            )}
                          >
                            {!entry.revisionName && (
                              <MenuItem value="" disabled>
                                <em>选择版本</em>
                              </MenuItem>
                            )}
                            {selectableRevs.map(rev => (
                              <MenuItem key={rev.metadata.name} value={rev.metadata.name}>
                                <Box>
                                  <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                                    {rev.metadata.name}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {new Date(rev.metadata.creationTimestamp).toLocaleString('zh-CN', {
                                      year: 'numeric', month: '2-digit', day: '2-digit',
                                      hour: '2-digit', minute: '2-digit', second: '2-digit',
                                    })}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {creationTime && (
                          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                            {creationTime}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          size="small"
                          variant="outlined"
                          value={entry.percent}
                          onChange={e => handleTrafficPercentChange(index, parseInt(e.target.value, 10) || 0)}
                          inputProps={{ min: 0, max: 100, style: { textAlign: 'right', paddingRight: 8 } }}
                          InputProps={{
                            endAdornment: <Typography variant="body2" style={{ marginRight: 8 }}>%</Typography>,
                          }}
                          style={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => removeTrafficEntry(index)}
                          disabled={trafficEntries.length <= 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box mt={1}>
            <Typography
              variant="body2"
              color={trafficTotal === 100 ? 'textSecondary' : 'error'}
              style={{ fontWeight: trafficTotal !== 100 ? 600 : 400 }}
            >
              总计: {trafficTotal}% {trafficTotal !== 100 && '（必须等于 100%）'}
            </Typography>
          </Box>

          {executeError && (
            <Alert severity="error" style={{ marginTop: 8 }}>{executeError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrafficDialogOpen(false)} disabled={executing}>取消</Button>
          <Button
            onClick={executeTrafficSplit}
            color="primary"
            variant="contained"
            disabled={executing || trafficTotal !== 100 || trafficEntries.length === 0}
            startIcon={executing ? <CircularProgress size={16} /> : <PlayArrowIcon />}
          >
            {executing ? '提交中...' : '执行切流'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return {
    pipelinesLoading,
    refresh,
    executingPipelines,
    hasKnativeConfig,
    releasePipeline,
    trafficSplitPipeline,
    pipelines,
    openReleaseDialog,
    openTrafficDialog,
    pipelineRuns,
    releaseRuns,
    trafficRuns,
    renderStatusDots,
    renderLastRun,
    tektonClusterName,
    tektonNamespace,
    dialogs,
  };
};
